import { zodResolver } from '@hookform/resolvers/zod'
import { assert, Percentage, raise } from '@sparkdotfi/common-universal'
import { useCallback, useEffect, useState } from 'react'
import { UseFormReturn, useForm } from 'react-hook-form'

import { getBorrowableAssets, getDepositableAssets, imputeNativeAsset, sortByDecreasingBalances } from './assets'
import {
  FormFieldsForAssetClass,
  getDefaultFormValues,
  setDesiredLoanToValue,
  useFormFieldsForAssetClass,
} from './form/form'
import { mapFormTokensToReserves } from './form/mapFormTokensToReserves'
import { normalizeFormValues } from './form/normalization'
import { EasyBorrowFormSchema, getEasyBorrowFormValidator } from './form/validation'
import { ExistingPosition, PageState, PageStatus } from './types'
import { useCreateObjectives } from './useCreateObjectives'
import { useLiquidationDetails } from './useLiquidationDetails'
import { getChainConfigEntry } from '@/config/chain'
import { TokenWithValue } from '@/domain/common/types'
import { useConditionalFreeze } from '@/domain/hooks/useConditionalFreeze'
import { usePageChainId } from '@/domain/hooks/usePageChainId'
import { useSandbox } from '@/domain/sandbox'
import { Token } from '@/domain/types/Token'
import { useConnectedAddress } from '@/domain/wallet/useConnectedAddress'
import { useActions } from '@/features/actions/logic/useActions'
import { ActionsViewState } from '@/features/actions/types'
import { useAaveDataLayer } from '@/features/sparklend/common/data/market-info/aave-data-layer/useAaveDataLayer'
import { EPOCH_LENGTH } from '@/features/sparklend/common/data/market-info/consts'
import { LiquidationDetails } from '@/features/sparklend/common/data/market-info/getLiquidationDetails'
import { UserPositionSummary } from '@/features/sparklend/common/data/market-info/marketInfo'
import { updatePositionSummary } from '@/features/sparklend/common/data/market-info/updatePositionSummary'
import { useMarketInfo } from '@/features/sparklend/common/data/market-info/useMarketInfo'
import { useMarketWalletInfo } from '@/features/sparklend/common/data/useMarketWalletInfo'
import { RiskAcknowledgementInfo } from '@/features/sparklend/common/utils/liquidation-risk-warning/types'
import { useLiquidationRiskWarning } from '@/features/sparklend/common/utils/liquidation-risk-warning/useLiquidationRiskWarning'

export interface BorrowDetails {
  borrowToken: Token
  borrowRate: Percentage
}

export interface UseEasyBorrowResults {
  pageStatus: PageStatus
  form: UseFormReturn<EasyBorrowFormSchema>
  updatedPositionSummary: UserPositionSummary
  assetsToBorrowFields: FormFieldsForAssetClass
  assetsToDepositFields: FormFieldsForAssetClass
  setDesiredLoanToValue: (desiredLtv: Percentage) => void
  tokensToBorrow: TokenWithValue[]
  tokensToDeposit: TokenWithValue[]
  alreadyDeposited: ExistingPosition
  alreadyBorrowed: ExistingPosition
  liquidationDetails?: LiquidationDetails
  riskAcknowledgement: RiskAcknowledgementInfo
  borrowDetails: BorrowDetails
  guestMode: boolean
  openSandboxModal: () => void
  focusOnActionsPanel: (node: HTMLDivElement | null) => void
  actionsViewState: ActionsViewState
}

export function useEasyBorrow(): UseEasyBorrowResults {
  const { account, chainId: connectedChainId } = useConnectedAddress({ throwOnNotConnected: false })
  const { chainId } = usePageChainId()
  const guestMode = !account
  const { openSandboxModal } = useSandbox()
  const { aaveData } = useAaveDataLayer({ chainId })
  const { marketInfo } = useMarketInfo({ chainId })
  const { marketInfo: marketInfoIn1Epoch } = useMarketInfo({ timeAdvance: EPOCH_LENGTH, chainId })
  const { sparkLend, nativeAssetInfo } = getChainConfigEntry(marketInfo.chainId)
  const { defaultAssetToBorrow } = sparkLend ?? {}
  assert(defaultAssetToBorrow, 'defaultAssetToBorrow is required for easy borrow')

  const walletInfo = useMarketWalletInfo({ chainId })

  const [pageStatus, setPageStatus] = useState<PageState>('form')

  const userPositions = imputeNativeAsset(marketInfo, nativeAssetInfo)
  const alreadyDeposited = useConditionalFreeze(
    {
      tokens: userPositions
        .filter((position) => position.collateralBalance.gt(0))
        .filter((position) => position.reserve.usageAsCollateralEnabledOnUser)
        .map((position) => position.reserve.token),
      totalValueUSD: marketInfo.userPositionSummary.totalCollateralUSD,
    },
    pageStatus === 'confirmation',
  )
  const alreadyBorrowed = useConditionalFreeze(
    {
      tokens: userPositions
        .filter((position) => position.borrowBalance.gt(0))
        .map((position) => position.reserve.token),
      totalValueUSD: marketInfo.userPositionSummary.totalBorrowsUSD,
    },
    pageStatus === 'confirmation',
  )

  const depositableAssets = sortByDecreasingBalances(getDepositableAssets(userPositions, walletInfo))
  const borrowableAssets = getBorrowableAssets(marketInfo.reserves, walletInfo, chainId)
  const formAssets = userPositions.map((position) => ({
    token: position.reserve.token,
    balance: walletInfo.findWalletBalanceForToken(position.reserve.token),
  }))

  assert(depositableAssets.length > 0, 'No depositable assets')
  assert(borrowableAssets.length > 0, 'No borrowable assets')

  const defaultFormValues = getDefaultFormValues({
    depositableAssets,
    borrowableAssets,
    defaultAssetToBorrow,
  })

  const easyBorrowForm = useForm<EasyBorrowFormSchema>({
    resolver: zodResolver(
      getEasyBorrowFormValidator({
        walletInfo,
        marketInfo,
        aaveData,
        formAssets,
        guestMode,
        alreadyDeposited,
        nativeAssetInfo,
      }),
    ),
    defaultValues: defaultFormValues,
    mode: 'onChange',
  })
  const assetsToDepositFields = useFormFieldsForAssetClass({
    form: easyBorrowForm,
    marketInfo: marketInfoIn1Epoch, // because we calculate max values based on the future state
    assetsToPickFrom: depositableAssets,
    allAssets: formAssets,
    walletInfo,
    type: 'deposit',
  })
  const assetsToBorrowFields = useFormFieldsForAssetClass({
    form: easyBorrowForm,
    marketInfo,
    assetsToPickFrom: borrowableAssets,
    allAssets: formAssets,
    walletInfo,
    type: 'borrow',
  })
  const assetsToDepositFieldsFrozen = useConditionalFreeze(assetsToDepositFields, pageStatus === 'confirmation')
  const assetsToBorrowFieldsFrozen = useConditionalFreeze(assetsToBorrowFields, pageStatus === 'confirmation')

  const rawFormValues = easyBorrowForm.watch()
  const formValues = normalizeFormValues(rawFormValues, formAssets)
  const tokensToBorrow = formValues.borrows
  const tokensToDeposit = formValues.deposits.filter(({ value }) => value.gt(0))

  const actions = useCreateObjectives(formValues)

  const formValuesAsUnderlyingReserves = mapFormTokensToReserves({
    formValues,
    marketInfo,
  })
  const updatedUserSummary = useConditionalFreeze(
    updatePositionSummary({
      ...formValuesAsUnderlyingReserves,
      marketInfo,
      aaveData,
      nativeAssetInfo,
    }),
    pageStatus === 'confirmation',
  )

  const liquidationDetails = useLiquidationDetails({
    marketInfo,
    tokensToDeposit,
    tokensToBorrow: formValuesAsUnderlyingReserves.borrows.map((borrow) => ({
      token: borrow.reserve.token,
      value: borrow.value,
    })),
    liquidationThreshold: updatedUserSummary.currentLiquidationThreshold,
    freeze: pageStatus === 'confirmation',
  })

  const borrowToken = formValues.borrows[0]?.token ?? raise('No borrow token')
  const borrowDetails = {
    borrowToken,
    borrowRate: marketInfo.findOneReserveBySymbol(borrowToken.symbol).variableBorrowApy ?? raise('No borrow rate'),
  }

  // biome-ignore lint/correctness/useExhaustiveDependencies: no explanation
  useEffect(
    function revalidateFormOnNetworkChange() {
      // biome-ignore lint/suspicious/noConsole: debugging
      easyBorrowForm.trigger().catch(console.error)
    },
    [connectedChainId],
  )

  // biome-ignore lint/correctness/useExhaustiveDependencies: no explanation
  useEffect(() => {
    setPageStatus('form')
    easyBorrowForm.reset(defaultFormValues)
  }, [account, easyBorrowForm])

  const focusOnActionsPanel = useCallback((node: HTMLDivElement | null) => {
    if (!node) {
      return
    }
    node.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }, [])

  const { riskAcknowledgement, disableActionsByRisk } = useLiquidationRiskWarning({
    type: 'liquidation-warning-borrow',
    isFormValid: easyBorrowForm.formState.isValid,
    currentHealthFactor: marketInfo.userPositionSummary.healthFactor,
    updatedHealthFactor: updatedUserSummary.healthFactor,
  })

  const actionsEnabled = !disableActionsByRisk && pageStatus !== 'form'
  const { viewState: actionsViewState, reset: resetActions } = useActions({
    objectives: actions,
    context: {
      marketInfo,
      chainId,
    },
    onFinish: () => setPageStatus('success'),
    enabled: actionsEnabled,
  })

  const depositKey = JSON.stringify(tokensToDeposit.map((t) => [t.token.symbol, t.value.toString()]))
  const borrowKey = JSON.stringify(tokensToBorrow.map((t) => [t.token.symbol, t.value.toString()]))

  // biome-ignore lint/correctness/useExhaustiveDependencies: no explanation
  useEffect(() => {
    resetActions()
  }, [resetActions, depositKey, borrowKey])

  return {
    form: easyBorrowForm,
    updatedPositionSummary: updatedUserSummary,
    assetsToDepositFields: assetsToDepositFieldsFrozen,
    assetsToBorrowFields: assetsToBorrowFieldsFrozen,
    setDesiredLoanToValue(desiredLtv: Percentage) {
      setDesiredLoanToValue({
        control: easyBorrowForm,
        formValues: formValuesAsUnderlyingReserves,
        userPositionSummary: updatedUserSummary,
        desiredLtv,
      })
    },
    pageStatus: {
      actionsEnabled,
      state: pageStatus,
      onProceedToForm: () => setPageStatus('form'),
      goToSuccessScreen: () => setPageStatus('success'),
      submitForm: () => setPageStatus('confirmation'),
    },
    tokensToBorrow,
    tokensToDeposit,
    alreadyDeposited,
    alreadyBorrowed,
    liquidationDetails,
    borrowDetails,
    guestMode,
    openSandboxModal,
    focusOnActionsPanel,
    riskAcknowledgement,
    actionsViewState,
  }
}
